// src/features/dashboard/cache/dashboardCache.ts
//
// Lightweight cache-first store for dashboard data.
//
// This is deliberately small: an in-memory Map for the fastest reads
// within a session, backed by sessionStorage so a full page reload
// doesn't force every dashboard widget back to a loading skeleton.
// It is not a replacement for React Query / SWR -- just enough to
// unblock the cache-first + silent-refresh pattern in the interim.
// Any feature under src/features/dashboard can reuse this (spotlight
// today, the rest of useDashboardData once it talks to a real API).

const STORAGE_PREFIX = 'vania:dashboard-cache:';

interface CacheEntry<T> {
  value: T;
  storedAt: number;
}

const memoryCache = new Map<string, CacheEntry<unknown>>();

function storageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

function readFromSessionStorage<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(key));
    if (!raw) return null;
    return JSON.parse(raw) as CacheEntry<T>;
  } catch {
    // Corrupt entry or sessionStorage unavailable -- treat as a miss.
    return null;
  }
}

function writeToSessionStorage<T>(key: string, entry: CacheEntry<T>): void {
  try {
    window.sessionStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota, SSR).
    // The in-memory cache still works within this tab session; we just
    // lose persistence across a hard reload.
  }
}

function removeFromSessionStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(key));
  } catch {
    // ignore
  }
}

export const dashboardCache = {
  /** Returns the cached value for `key`, or null if nothing is cached. */
  get<T>(key: string): T | null {
    const inMemory = memoryCache.get(key) as CacheEntry<T> | undefined;
    if (inMemory) {
      return inMemory.value;
    }

    const persisted = readFromSessionStorage<T>(key);
    if (persisted) {
      // Warm the in-memory layer so subsequent reads this session are cheap.
      memoryCache.set(key, persisted);
      return persisted.value;
    }

    return null;
  },

  /** Stores `value` under `key`, in memory and in sessionStorage. */
  set<T>(key: string, value: T): void {
    const entry: CacheEntry<T> = { value, storedAt: Date.now() };
    memoryCache.set(key, entry);
    writeToSessionStorage(key, entry);
  },

  /** Drops the cached value for `key` (e.g. on logout or a forced refresh). */
  invalidate(key: string): void {
    memoryCache.delete(key);
    removeFromSessionStorage(key);
  },
};

/**
 * Builds a cache key scoped to the current identity, so switching accounts
 * in the same browser session never shows one representative's cached data
 * to another. Falls back to a shared "anon" bucket when no identity is
 * available yet (e.g. before auth has resolved).
 */
export function scopedCacheKey(
  namespace: string,
  identity: string | null | undefined
): string {
  return `${namespace}:${identity ?? 'anon'}`;
}
